import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Grid, Alert, Box, Typography, SxProps, alpha, lighten, useTheme, useMediaQuery, Avatar
} from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';
import { Pagination, Controller, Virtual } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { DateTime } from 'luxon';
import {
  IoEyeOffOutline, IoEyeOutline, IoTimeOutline, IoPeopleOutline, IoChevronBack, IoCheckmarkCircleOutline, 
  IoChevronForward,
  IoCalendarClearOutline,
  IoTrashOutline,
  IoWarningOutline,
  IoHelpCircleOutline,
  IoEllipseOutline,
  IoCreateOutline,
  IoChatbubble,
  IoChatbubbleOutline,
  IoAdd,
} from 'react-icons/io5';
import {
  CabButton, CabControlLabel, CabDropdown, CabIcon, CabSwitch, CabToggleChip, CabTooltip, CabSpinner
} from '@CabComponents';
import colors from '../../../colors';
import { MEETING_STATUS_LABELS } from '../../../constants';
import {
  MeetingPollPriority, ParticipantsWithSelections, ParticipantWithSelections, SelectedSlot
} from '../../../store';
import { useElementWidth } from '../../../utils/hooks';


interface HeaderProps {
  creatorName: string;
  creatorEmail: string;
  name: string;
  description: string;
  durationText: string;
  expectedParticipants: number;
  participantLength: number;
  onUpdateStatus?: (status: number) => Promise<void>;
}

export const Header = ({
  creatorName, creatorEmail, name, description, durationText, expectedParticipants, participantLength,
  onUpdateStatus,
}: HeaderProps): ReactElement => {
  const [showDescription, setShowDescription] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between"
      }}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: "column"
        }}>
        <Box>
          <Typography variant="h1">{name}</Typography>
        </Box>
        <Box>
          {description && (
            <>
              <CabButton buttonType='tertiary' color='accent' 
                onClick={() => setShowDescription(!showDescription)}
                icon={<CabIcon Icon={showDescription ? IoEyeOffOutline : IoEyeOutline}/>} sx={{width: '178px'}}>
                {showDescription ? 'Hide Description' : 'See Description'}
              </CabButton>
              {showDescription && (
                <Typography color={colors.black600} sx={{
                  whiteSpace: 'pre-wrap'
                }}>{description}</Typography>
              )}
            </>
          )}
        </Box>
        {creatorName && (
          <Box
            sx={{
              display: "flex",
              maxWidth: '630px',
              flexWrap: 'wrap',
              width: '80%',
              gap: 1.5
            }}>
            <Box
              sx={{
                display: "flex",
                gap: 0.5
              }}>
              <Typography color={colors.black600} sx={{
                whiteSpace: "nowrap"
              }}>Created by</Typography>
              <Typography
                color={colors.black600}
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap"
                }}>
                {creatorName} ({creatorEmail})
              </Typography>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 1
          }}>
          <CabToggleChip
            title={durationText}
            label={durationText}
            icon={<CabIcon Icon={IoTimeOutline} sx={{ fontSize: 20, paddingBottom: '2px' }} />}
            sx={{ backgroundColor: colors.purpleLight, borderColor: colors.purpleLight, height: 30, fontWeight: 500, }}
          />
          <CabToggleChip
            title={`${participantLength}/${expectedParticipants} Responses`}
            label={`${participantLength}/${expectedParticipants} Responses`}
            icon={<CabIcon Icon={IoPeopleOutline} sx={{ fontSize: 20, paddingBottom: '2px' }} />}
            sx={{ backgroundColor: colors.purpleLight, borderColor: colors.purpleLight, height: 30, fontWeight: 500 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface PollTableLegendProps {
  hidePopular?: boolean;
}

interface GutterWidths {
  leftGutterWidth: number;
  rightGutterWidth: number;
}

const useResponsiveGutterWidths = (admin: boolean = false): GutterWidths => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const leftGutterWidth = useMemo(() => {
    if (!admin && isSmallScreen) return 20;
    // Give participant names a little more room so they wrap less often.
    if (isSmallScreen) return 84;
    if (isMediumScreen) return 120;
    return 160;
  }, [isSmallScreen, isMediumScreen, admin]);

  const rightGutterWidth = useMemo(() => {
    if (isSmallScreen) return 20;
    if (isMediumScreen) return 40;
    return 50;
  }, [isSmallScreen, isMediumScreen]);

  return { leftGutterWidth, rightGutterWidth };
};

export const PollTableLegend = ({hidePopular}: PollTableLegendProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}>
      <Box />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 3
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center"
          }}>
          <Box
            sx={{
              minWidth: 22,
              width: 22,
              height: 22,
              borderRadius: 2,
              borderStyle: 'solid',
              borderWidth: 2,
              backgroundColor: colors.forest500,
              borderColor: colors.forest700
            }} />
          <Typography>Available</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center"
          }}>
          <Box
            sx={{
              minWidth: 22,
              width: 22,
              height: 22,
              borderRadius: 2,
              borderStyle: 'solid',
              borderWidth: 2,
              backgroundColor: colors.chamomile300,
              borderColor: colors.chamomile700
            }} />
          <Typography>{isSmallScreen ? 'Potentially' : 'Potentially Available'}</Typography>
        </Box>
        {!hidePopular &&
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center"
            }}>
            <Box
              sx={{
                minWidth: 22,
                width: 22,
                height: 22,
                borderRadius: 2,
                borderStyle: 'solid',
                borderWidth: 2,
                borderColor: colors.lavendar300,
                backgroundColor: colors.lavendar100
              }} />
            <Typography>Popular Times</Typography>
          </Box>
        }
      </Box>
    </Box>
  );
};


export interface StatusDropdownProps {
  status: number;
  onUpdateStatus: (status: number) => Promise<void>;
  onOpen?: () => void;
  onClose?: () => void;
  sx?: SxProps;
}

export const StatusDropdown = ({ status, onUpdateStatus, onOpen, onClose, sx }: StatusDropdownProps) => {
  const [statusLoading, setStatusLoading] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: .5
      }}>
      <CabDropdown
        value={Object.keys(MEETING_STATUS_LABELS).find(key => Number(key) === status)} 
        placeholder="Select Status"
        onOpen={onOpen}
        onClose={onClose}
        options={Object.keys(MEETING_STATUS_LABELS).map((statusId) => ({
          value: statusId,
          label: MEETING_STATUS_LABELS[Number(statusId)],
          // onClick: e => e.stopPropagation(),
        }))}
        onChange={async (e) => {
          setStatusLoading(true);
          await onUpdateStatus(Number(e.target.value));
          setStatusLoading(false);
        }}
        sx={{ backgroundColor: colors.black100, ...sx }}
      />
      {statusLoading && <CabSpinner scale={1} />}
    </Box>
  );
};

export type Slot = { start: DateTime, end: DateTime };

export interface PollTableProps {
  votable: boolean;
  name: string;
  participants: ParticipantsWithSelections;
  timezoneConvertedSlots: (Slot & { index: number; converted: Slot })[];
  onCheckSlot: (email: string, index: number, priority: MeetingPollPriority | null) => void;
  onConvertToMeeting?: (start: DateTime, end: DateTime) => void;
  onRemoveSlot?: (index: number) => void;
  slotIndexesToRemove?: Set<number>;
  startTime?: string|null;
  selectedSlots?: { [email: string]: Map<number, SelectedSlot | null> };
  highlightTopPicks?: boolean;
  inAdmin?: boolean;
  // returningUserEmailHashes?: {[hash: string]: string};
  activeParticipants?: { email: string; name: string; required?: boolean }[];
  loading?: boolean;
  showEmailsOverride?: boolean;
  onShowEmailsOverride?: (show: boolean) => void;
  calendarOpen?: boolean;
  calendarDate?: DateTime;
  onEndReached?: (value: boolean) => void;
  enhancePollTableCursors?: boolean;
  onDateChange?: (date: DateTime) => void;
  sx?: SxProps;
  inProgress?: boolean;
  showVoteCounts?: boolean;
  onOpenSlotNoteModal?: (payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlot;
    mode: "add" | "edit";
  }) => void;
  onRemoveSlotNote?: (payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlot;
  }) => void;
  allDaySlots?: boolean;
}

export const PollTable = ({
  votable, name, participants, timezoneConvertedSlots: convertedSlots, onCheckSlot: handleCheckSlot,
  onConvertToMeeting, startTime, selectedSlots, highlightTopPicks, inProgress,
  inAdmin, activeParticipants = [], loading, showEmailsOverride, onShowEmailsOverride,
  calendarOpen, calendarDate, onEndReached, enhancePollTableCursors = false, onDateChange, sx, onRemoveSlot,
  slotIndexesToRemove, showVoteCounts = false,
  onOpenSlotNoteModal, onRemoveSlotNote, allDaySlots = false,
}: PollTableProps): ReactElement => {
  const { leftGutterWidth, rightGutterWidth } = useResponsiveGutterWidths(inAdmin);
  const [showEmails, setShowEmails] = useState(showEmailsOverride || false);
  const containerRef = useRef<Element | null>(null);
  const [controllerSwiper, setControllerSwiper] = useState<SwiperType | null>(null);
  const [controlledSwipers, setControlledSwipers] = useState<SwiperType[]>([]);

  useEffect(() => {
    if (showEmailsOverride != null && showEmails !== showEmailsOverride) {
      setShowEmails(showEmailsOverride);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmailsOverride]);
  
  const sortedSlots = useMemo(() => (
    convertedSlots.sort((a, b) => a.start.toMillis() - b.start.toMillis())
  ), [convertedSlots]);

  const slots = useMemo(() => (
    sortedSlots.filter(slot => !slotIndexesToRemove?.has(slot.index))
  ), [sortedSlots, slotIndexesToRemove]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const width = useElementWidth(containerRef, 1000, 25);

  let slidesPerView = Math.max(Math.floor(width / (isSmallScreen && !inAdmin ? 102 : 142)), 1);
  let slidesPerGroup = slidesPerView > 1 ? slidesPerView - 1 : slidesPerView;
  slidesPerView = Math.min(slidesPerView, slots.length || 1);
  slidesPerGroup = Math.min(slidesPerGroup, slots.length || 1);
  const maxWidth = slots.length * 150 + leftGutterWidth + rightGutterWidth;
  
  // Enable virtual slides when there are many slots
  const shouldUseVirtual = slots.length > 50;
  
  const votableSlotsDict: { [key: number]: boolean } = useMemo(() => {
    const dict: { [key: number]: boolean } = {};
    convertedSlots.forEach(slot => {
      dict[Number(slot.start.toMillis())] = true;
    });
    return dict;
  }, [convertedSlots]);

  const slotFrequency = useMemo(() => {
    const initialValue: {
      [key: number]: { [MeetingPollPriority.AVAILABLE]: number; [MeetingPollPriority.POTENTIAL]: number; }
    } = {};

    return participants.flatMap(participantsItr => participantsItr.selectedSlots)
      .map(slot => ({
        priority: slot.priority ?? MeetingPollPriority.AVAILABLE,
        startMillis: slot.start.toMillis()
      }))
      .filter(mappedSlot => votableSlotsDict[mappedSlot.startMillis])
      .reduce((prevSlot, currentMappedSlot) => {
        const slotKey = currentMappedSlot.startMillis;
        const priority = currentMappedSlot.priority;
        const count = 1;

        if (!prevSlot[slotKey]) {
          prevSlot[slotKey] = {
            [MeetingPollPriority.AVAILABLE]: 0,
            [MeetingPollPriority.POTENTIAL]: 0,
          };
        }

        if (priority === MeetingPollPriority.AVAILABLE) {
          prevSlot[slotKey][MeetingPollPriority.AVAILABLE] += count;
        } else if (priority === MeetingPollPriority.POTENTIAL) {
          prevSlot[slotKey][MeetingPollPriority.POTENTIAL] += count;
        }

        return prevSlot;
      }, initialValue);
  }, [participants, votableSlotsDict]);

  const topSlots = useMemo(() => {
    let max = 0;
    Object.values(slotFrequency).forEach(freq => {
      const availableVotes = freq?.[MeetingPollPriority.AVAILABLE] || 0;
      const potentialVotes = freq?.[MeetingPollPriority.POTENTIAL] || 0;
      const totalFreq = availableVotes + potentialVotes;
      if (totalFreq > max) max = totalFreq;
    });
    return Object.keys(slotFrequency).map(timeslot => Number(timeslot)).filter((timeslot) => {
      const freq = slotFrequency[timeslot];
      const availableVotes = freq?.[MeetingPollPriority.AVAILABLE] || 0;
      const potentialVotes = freq?.[MeetingPollPriority.POTENTIAL] || 0;
      const totalFreq = availableVotes + potentialVotes;
      return max > 0 && totalFreq === max;
    });
  }, [slotFrequency]);

  const sortParticipantsAlphabetically = useCallback((
    a: { name?: string | null; email?: string | null },
    b: { name?: string | null; email?: string | null }
  ) => {
    const aLabel = (a.name || a.email || '').toLowerCase();
    const bLabel = (b.name || b.email || '').toLowerCase();
    return aLabel.localeCompare(bLabel);
  }, []);

  const sortedActiveParticipants = useMemo(() => (
    [...activeParticipants].sort(sortParticipantsAlphabetically)
  ), [activeParticipants, sortParticipantsAlphabetically]);

  const filterParticipants = useMemo(() => (
    participants
      .filter(p => !activeParticipants.some(ap => ap.email === p.email))
      .sort(sortParticipantsAlphabetically)
  ), [participants, activeParticipants, sortParticipantsAlphabetically]);
  const hasVisibleOptionalParticipants = useMemo(() => (
    sortedActiveParticipants.some(participant => participant.required === false)
    || filterParticipants.some(participant => participant.required === false)
  ), [sortedActiveParticipants, filterParticipants]);

  const handlePrev = useCallback(() => {
    if (!controllerSwiper) return;
    if (!calendarOpen || !calendarDate || !onDateChange) {
      controllerSwiper.slidePrev();
      return;
    }

    const currentEndIndex = controllerSwiper.activeIndex + slidesPerView - 1;
    const currentlyVisibleSlots = slots.slice(controllerSwiper.activeIndex, currentEndIndex + 1);

    const prevDateInView = [...currentlyVisibleSlots].reverse().find(slot =>
      slot.converted.start.startOf('day').toMillis() < calendarDate.startOf('day').toMillis()
    )?.converted.start.startOf('day');

    if (prevDateInView) {
      const targetIndex = slots.findIndex(s => (
        s.converted.start.startOf('day').toMillis() === prevDateInView.toMillis()
      ));
      if (targetIndex !== -1) {
        const isTargetAlreadyVisibleBefore = currentlyVisibleSlots.some(s =>
          s.converted.start.startOf('day').toMillis() === prevDateInView.toMillis() &&
          s.converted.start.startOf('day').toMillis() < calendarDate.startOf('day').toMillis()
        );

        if (prevDateInView.toMillis() === calendarDate.startOf('day').toMillis() || isTargetAlreadyVisibleBefore) {
          controllerSwiper.slidePrev();
        } else {
          controllerSwiper.slideTo(targetIndex);
          if (onDateChange) onDateChange(prevDateInView);
        }

      } else {
        controllerSwiper.slidePrev();
      }
    } else {
      controllerSwiper.slidePrev();
      
      const newFirstVisibleSlot = slots[controllerSwiper.activeIndex];
      if (newFirstVisibleSlot && onDateChange) {
        const newDate = newFirstVisibleSlot.converted.start.startOf('day');
        if (calendarDate.toMillis() !== newDate.toMillis()) {
          onDateChange(newDate);
        }
      }
    }
  }, [controllerSwiper, slots, slidesPerView, calendarDate, onDateChange, calendarOpen]);

  const handleNext = useCallback(() => {
    if (!controllerSwiper) return;
    if (!calendarOpen || !calendarDate || !onDateChange) {
      controllerSwiper.slideNext();
      return;
    }

    const currentStartIndex = controllerSwiper.activeIndex;
    const currentlyVisibleSlots = slots.slice(currentStartIndex, currentStartIndex + slidesPerView);

    const nextDateInView = currentlyVisibleSlots.find(slot =>
      slot.converted.start.startOf('day').toMillis() > calendarDate.startOf('day').toMillis()
    )?.converted.start.startOf('day');

    if (nextDateInView) {
      const targetIndex = slots.findIndex(s => (
        s.converted.start.startOf('day').toMillis() === nextDateInView.toMillis()
      ));
      if (targetIndex !== -1) {
        if (slots[currentStartIndex]?.converted.start.startOf('day').toMillis() === nextDateInView.toMillis()) {
          controllerSwiper.slideNext();
        } else {
          controllerSwiper.slideTo(targetIndex);
          if (onDateChange) onDateChange(nextDateInView);
        }
      } else {
        controllerSwiper.slideNext();
      }

    } else {
      controllerSwiper.slideNext();
      const newFirstVisibleSlotIndex = controllerSwiper.activeIndex;
      const actualNewIndex = Math.min(newFirstVisibleSlotIndex, slots.length - slidesPerView);

      const newFirstVisibleSlot = slots[actualNewIndex];
      if (newFirstVisibleSlot && onDateChange) {
        const newDate = newFirstVisibleSlot.converted.start.startOf('day');
        if (calendarDate.toMillis() !== newDate.toMillis()) {
          onDateChange(newDate);
        }
      }
    }
  }, [controllerSwiper, slots, slidesPerView, calendarDate, onDateChange, calendarOpen]);

  // Register a controlled swiper
  const registerControlledSwiper = useCallback((swiper: SwiperType) => {
    if (swiper) {
      setControlledSwipers(prev => [...prev, swiper]);
    }
  }, []);

  useEffect(() => {
    if (!controllerSwiper || !calendarOpen || !calendarDate) return;

    const targetIndex = slots.findIndex(s => s.converted.start.startOf('day').toMillis() === calendarDate.toMillis());

    if (targetIndex !== -1) {
      const currentStartIndex = controllerSwiper.activeIndex;
      const currentEndIndex = currentStartIndex + slidesPerView - 1;
      const isVisible = targetIndex >= currentStartIndex && targetIndex <= currentEndIndex;

      if (!isVisible) {
        controllerSwiper.slideTo(targetIndex);
      }
    }
  }, [calendarOpen, calendarDate, controllerSwiper, slots, slidesPerView]);

  return (
    <Box
      ref={containerRef}
      sx={[{
        maxWidth: maxWidth
      }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {/* For some reason making the slides virtual will completely override inline styles,
      so we need to use this style tag to preserve them */}
      {shouldUseVirtual && (
        <style>{`
          .swiper-slide {
            box-sizing: border-box !important;
          }
          /* Preserve borders for Current Votes section when virtual slides override inline styles */
          .swiper-slide-with-border {
            border: 1px solid rgb(238, 238, 239) !important;
          }
          .swiper-slide-with-border[data-is-first-row="true"] {
            border-top: none !important;
          }
          .swiper-slide-with-border[data-is-last-row="true"] {
            border-bottom: none !important;
          }
        `}</style>
      )}
      {loading || slots.length !== 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              overflow: "hidden",
              backgroundColor: 'transparent',
              borderColor: colors.black100,
              borderRadius: 3,
              maxWidth: '100%',
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                overflow: "hidden"
              }}>
              <Box
                sx={{
                  flexGrow: 1,
                  paddingTop: 2,
                  width: '100%',
                  maxWidth: '100%'
                }}>
                <TimeSlotHeaderSwiper
                  slots={slots}
                  slidesPerView={slidesPerView}
                  slidesPerGroup={slidesPerGroup}
                  calendarDate={calendarDate}
                  calendarOpen={calendarOpen}
                  onDateChange={onDateChange}
                  setControllerSwiper={setControllerSwiper}
                  onConvertToMeeting={onConvertToMeeting}
                  onRemoveSlot={onRemoveSlot}
                  startTime={startTime}
                  inProgress={inProgress}
                  highlightTopPicks={highlightTopPicks}
                  allDaySlots={allDaySlots}
                  topSlots={topSlots}
                  slotFrequency={slotFrequency}
                  slotIndexesToRemove={slotIndexesToRemove}
                  handlePrev={handlePrev}
                  handleNext={handleNext}
                  controlledSwipers={controlledSwipers}
                  onEndReached={onEndReached}
                  inAdmin={inAdmin}
                  shouldUseVirtual={shouldUseVirtual}
                  showVoteCounts={showVoteCounts}
                  participantsCount={participants.length}
                />

                {sortedActiveParticipants.map((participant, idx) => (
                  <ParticipantRowSwiper
                    swiperStyle={{ margin: 0, overflow: 'hidden' }}
                    key={participant.email}
                    idx={idx}
                    participant={participant}
                    slots={slots}
                    slidesPerView={slidesPerView}
                    slidesPerGroup={slidesPerGroup}
                    onRegisterSwiper={registerControlledSwiper}
                    votable={votable}
                    onCheckSlot={handleCheckSlot}
                    selectedSlots={selectedSlots}
                    onOpenSlotNoteModal={onOpenSlotNoteModal}
                    onRemoveSlotNote={onRemoveSlotNote}
                    calendarOpen={calendarOpen}
                    calendarDate={calendarDate}
                    showPagination={false}
                    inAdmin={inAdmin}
                    shouldUseVirtual={shouldUseVirtual}
                  />
                ))}

                {activeParticipants.length > 0 && filterParticipants.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}>
                    <PaginationSwiper
                      slots={slots}
                      slidesPerView={slidesPerView}
                      slidesPerGroup={slidesPerGroup}
                      onRegisterSwiper={registerControlledSwiper}
                      inAdmin={inAdmin}
                      shouldUseVirtual={shouldUseVirtual}
                    />
                  </Box>
                )}
                
                {filterParticipants.length > 0 && (
                  <>
                    <Box
                      sx={{
                        marginLeft: `${leftGutterWidth + 8}px`,
                        marginBottom: 2
                      }}>
                      {!inAdmin && (
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: 18,
                            fontWeight: 600
                          }}>
                          Current Votes
                        </Typography>
                      )}
                    </Box>

                    {filterParticipants.map((p, idx) => (
                      <ParticipantRowSwiper
                        key={p.emailHash || p.email || idx}
                        idx={idx}
                        isInactiveBlock={true}
                        participant={p}
                        slots={slots}
                        slidesPerView={slidesPerView}
                        slidesPerGroup={slidesPerGroup}
                        onRegisterSwiper={registerControlledSwiper}
                        onOpenSlotNoteModal={onOpenSlotNoteModal}
                        onRemoveSlotNote={onRemoveSlotNote}
                        calendarOpen={calendarOpen}
                        calendarDate={calendarDate}
                        showEmails={showEmails}
                        inAdmin={inAdmin}
                        showPagination={false}
                        shouldUseVirtual={shouldUseVirtual}
                        isFirstRow={idx === 0}
                        isLastRow={idx === filterParticipants.length - 1}
                        swiperStyle={{
                          margin: 0,
                          borderTop: idx === 0 ? `2px solid ${colors.black100}` : undefined,
                          borderBottom: idx === filterParticipants.length - 1
                            ? `2px solid ${colors.black100}`
                            : undefined,
                          borderTopLeftRadius: idx === 0 ? 8 : 0,
                          borderTopRightRadius: idx === 0 ? 8 : 0,
                          borderBottomLeftRadius: idx === filterParticipants.length - 1 ? 8 : 0,
                          borderBottomRightRadius: idx === filterParticipants.length - 1 ? 8 : 0,
                        }}
                        swiperSlideStyle={{
                          border: `1px solid ${colors.black100}`,
                          borderTop: idx !== 0 ? `1px solid ${colors.black100}` : 'none',
                          borderBottom: idx !== filterParticipants.length - 1 ? `1px solid ${colors.black100}` : 'none',
                        }}
                      />
                    ))}
                    {hasVisibleOptionalParticipants && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                          marginTop: 1,
                          marginBottom: 1
                        }}>
                        <Box
                          sx={{
                            width: leftGutterWidth,
                            minWidth: leftGutterWidth
                          }}>
                          <Typography
                            color={colors.black700}
                            sx={{
                              fontSize: 12,
                              fontWeight: 500
                            }}>
                            *Optional Attendee
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Grid container>
            <Grid size={{ xs: 12, sm: 9, md: 7 }}>
              <Alert severity="warning" sx={{ minWidth: 250 }}>
                <span>{loading ? 'Loading time slots...' : 'No time slots available.'}</span>
              </Alert>
            </Grid>
          </Grid>
          {inAdmin &&
            <Box>
              <CabControlLabel
                label="Show Emails"
                labelPlacement='end'
                control={<CabSwitch checked={showEmails} onChange={() => {
                  if (onShowEmailsOverride) {
                    onShowEmailsOverride(!showEmails);
                  } else {
                    setShowEmails(!showEmails);
                  }
                }} />}
              />
            </Box>
          }
        </>
      )}
    </Box>
  );
};

interface TimeSlotHeaderSwiperProps {
  slots: (Slot & { index: number; converted: Slot })[];
  slidesPerView: number;
  slidesPerGroup: number;
  calendarDate?: DateTime;
  calendarOpen?: boolean;
  onDateChange?: (date: DateTime) => void;
  onConvertToMeeting?: (start: DateTime, end: DateTime) => void;
  onRemoveSlot?: (index: number) => void;
  startTime?: string | null;
  inProgress?: boolean;
  highlightTopPicks?: boolean;
  topSlots: number[];
  slotFrequency: { [key: number]: {
    [MeetingPollPriority.AVAILABLE]: number; [MeetingPollPriority.POTENTIAL]: number; }
  };
  slotIndexesToRemove?: Set<number>;
  setControllerSwiper: (swiper: SwiperType | null) => void;
  handlePrev: () => void;
  handleNext: () => void;
  controlledSwipers: SwiperType[];
  onEndReached?: (value: boolean) => void;
  inAdmin?: boolean;
  shouldUseVirtual?: boolean;
  showVoteCounts?: boolean;
  participantsCount?: number;
  allDaySlots?: boolean;
}

const TimeSlotHeaderSwiper = ({
  slots, slidesPerView, slidesPerGroup, calendarDate, calendarOpen, onDateChange, setControllerSwiper,
  onConvertToMeeting, startTime, inProgress, onRemoveSlot, slotIndexesToRemove, highlightTopPicks, topSlots,
  slotFrequency, handlePrev, handleNext, controlledSwipers, onEndReached, inAdmin, shouldUseVirtual = false,
  showVoteCounts = false, participantsCount = 0, allDaySlots = false,
}: TimeSlotHeaderSwiperProps): ReactElement => {
  const { leftGutterWidth, rightGutterWidth } = useResponsiveGutterWidths(inAdmin);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const modules = useMemo(() => {
    const mods = [Controller];
    if (shouldUseVirtual) {
      mods.push(Virtual);
    }
    return mods;
  }, [shouldUseVirtual]);

  const checkEndReached = useCallback((swiper: SwiperType) => {
    // swiper visibleSlidesIndex is not always initially correct, so we check after a short delay
    setTimeout(() => {
      // @ts-expect-error visibleSlidesIndex is not typed
      if (!swiper.visibleSlidesIndexes || swiper.visibleSlidesIndexes.includes(swiper.slides.length - 1)) {
        onEndReached?.(true);
      }
    }, 1000);
  }, [onEndReached]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 1,
        width: "100%",
        overflow: 'hidden'
      }}>
      <Box
        sx={{
          width: leftGutterWidth,
          minWidth: leftGutterWidth,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end"
        }}>
        <CabButton
          buttonType='text'
          icon={<CabIcon Icon={IoChevronBack} />} onClick={handlePrev}
          sx={{ minWidth: 10, width: 35, paddingRight: 0, paddingLeft: 1 }}
        />
      </Box>
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <Swiper
          style={{ width: '100%' }}
          modules={modules}
          onSwiper={(swiper) => {
            setControllerSwiper(swiper);
            checkEndReached(swiper);
          }}
          slidesPerView={slidesPerView}
          slidesPerGroup={slidesPerGroup}
          watchSlidesProgress
          allowTouchMove={isSmallScreen}
          resizeObserver
          controller={{ control: controlledSwipers }}
          virtual={shouldUseVirtual ? {
            enabled: true,
            cache: false,
            addSlidesBefore: 5,
            addSlidesAfter: 5,
          } : undefined}
          onInit={(swiper) => {
            checkEndReached(swiper);
          }}
          onActiveIndexChange={(swiper) => {
            checkEndReached(swiper);
            
            if (!onDateChange || !calendarDate || !calendarOpen) return;

            const currentlyVisibleSlots = slots.filter((slot, i) => (
              i >= swiper.activeIndex && (i < swiper.activeIndex + slidesPerView)
            ));
            const visibleDayMillis = currentlyVisibleSlots.map(slot => (
              slot.converted.start.startOf('day').toMillis()
            ));
            const firstVisibleSlotDate = slots[swiper.activeIndex]?.converted.start.startOf('day');

            if (firstVisibleSlotDate && !visibleDayMillis.includes(calendarDate.startOf('day').toMillis())) {
              onDateChange(firstVisibleSlotDate);
            }
          }}
        >
          {slots.map((slot, slotIdx) => {
            const isSelectedDate = calendarDate ? slot.start.hasSame(calendarDate, 'day') : false;
            const isTopSlot = (highlightTopPicks ?? false) && topSlots.includes(slot.start.toMillis());

            return (
              <SwiperSlide
                key={`header-${slot.converted.start.toISO()}-${slot.converted.end.toISO()}`}
                virtualIndex={shouldUseVirtual ? slotIdx : undefined}
                style={{ width: 'auto', maxWidth: '200px', overflow: 'hidden', zIndex: 100 - slotIdx }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column"
                  }}>
                  <TimeSlotOption
                    key={`timeslot-${slot.index}`}
                    slot={slot}
                    isDateSelected={isSelectedDate || !calendarOpen}
                    onConvertToMeeting={onConvertToMeeting}
                    onRemoveSlot={onRemoveSlot}
                    startTime={startTime}
                    inProgress={inProgress}
                    isCheckedAvailable={false}
                    isCheckedPotential={false}
                    isPrimaryHighlighted={isTopSlot}
                    isMarkedForRemoval={slotIndexesToRemove?.has(slot.index) ?? false}
                    totalAvailableVotes={
                      slotFrequency[slot.converted.start.toMillis()]?.[MeetingPollPriority.AVAILABLE] || 0
                    }
                    totalPotentialVotes={
                      slotFrequency[slot.converted.start.toMillis()]?.[MeetingPollPriority.POTENTIAL] || 0
                    }
                    date={null}
                    showVoteCounts={showVoteCounts}
                    participantsCount={participantsCount}
                    allDaySlots={allDaySlots}
                  />
                </Box>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>
      <Box
        sx={{
          width: rightGutterWidth,
          minWidth: rightGutterWidth,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        }}>
        <CabButton
          buttonType='text'
          sx={{ minWidth: 10, width: 35, paddingRight: 0, paddingLeft: 1.5 }}
          icon={<CabIcon Icon={IoChevronForward} />}
          onClick={handleNext}
        />
      </Box>
    </Box>
  );
};

interface ParticipantRowSwiperProps {
  participant: { email: string; name: string; required?: boolean; } | ParticipantWithSelections;
  idx: number;
  isInactiveBlock?: boolean;
  slots: (Slot & { index: number; converted: Slot })[];
  slidesPerView: number;
  slidesPerGroup: number;
  onRegisterSwiper?: (swiper: SwiperType) => void;
  votable?: boolean;
  onCheckSlot?: (email: string, index: number, priority: MeetingPollPriority | null) => void;
  selectedSlots?: { [email: string]: Map<number, SelectedSlot | null> };
  onOpenSlotNoteModal?: (payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlot;
    mode: "add" | "edit";
  }) => void;
  onRemoveSlotNote?: (payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlot;
  }) => void;
  calendarOpen?: boolean;
  calendarDate?: DateTime;
  showPagination?: boolean;
  showEmails?: boolean;
  inAdmin?: boolean;
  swiperStyle?: React.CSSProperties;
  swiperSlideStyle?: React.CSSProperties;
  shouldUseVirtual?: boolean;
  isFirstRow?: boolean;
  isLastRow?: boolean;
}

const ParticipantRowSwiper = ({
  participant, idx, isInactiveBlock = false, slots, slidesPerView, slidesPerGroup,
  onRegisterSwiper, votable, onCheckSlot, selectedSlots, calendarOpen, calendarDate,
  showPagination = false, showEmails, inAdmin, swiperStyle, swiperSlideStyle,
  shouldUseVirtual = false, isFirstRow, isLastRow,
  onOpenSlotNoteModal, onRemoveSlotNote,
}: ParticipantRowSwiperProps): ReactElement => {
  const { leftGutterWidth, rightGutterWidth } = useResponsiveGutterWidths(inAdmin);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const p = participant;
  const inactiveParticipant = isInactiveBlock && 'selectedSlots' in p;
  const [openInteractiveTooltipKey, setOpenInteractiveTooltipKey] = useState<string | null>(null);
  const interactiveTooltipCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const modules = useMemo(() => {
    const mods = [Controller];
    if (showPagination) {
      mods.push(Pagination);
    }
    if (shouldUseVirtual) {
      mods.push(Virtual);
    }
    return mods;
  }, [showPagination, shouldUseVirtual]);

  const swiperRef = useRef<SwiperType | null>(null);

  const clearInteractiveTooltipCloseTimeout = useCallback(() => {
    if (interactiveTooltipCloseTimeoutRef.current) {
      clearTimeout(interactiveTooltipCloseTimeoutRef.current);
      interactiveTooltipCloseTimeoutRef.current = null;
    }
  }, []);

  const openInteractiveTooltip = useCallback((tooltipKey: string) => {
    clearInteractiveTooltipCloseTimeout();
    setOpenInteractiveTooltipKey(tooltipKey);
  }, [clearInteractiveTooltipCloseTimeout]);

  const scheduleCloseInteractiveTooltip = useCallback(() => {
    clearInteractiveTooltipCloseTimeout();
    interactiveTooltipCloseTimeoutRef.current = setTimeout(() => {
      setOpenInteractiveTooltipKey(null);
    }, 250);
  }, [clearInteractiveTooltipCloseTimeout]);

  useEffect(() => {
    if (swiperRef.current && onRegisterSwiper) {
      onRegisterSwiper(swiperRef.current);
    }
  }, [onRegisterSwiper]);

  useEffect(() => clearInteractiveTooltipCloseTimeout, [clearInteractiveTooltipCloseTimeout]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 1
      }}>
      <Box
        sx={{
          width: leftGutterWidth,
          minWidth: leftGutterWidth,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          overflow: 'hidden'
        }}>
        <Box
          sx={{
            justifyContent: "flex-start",
            overflow: 'hidden',
            width: '100%',
            display: isSmallScreen && !inAdmin ? 'none' : 'flex'
          }}>
          <Typography sx={{
            color: colors.black700,
            fontWeight: 500,
            wordSpacing: '2px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.2em',
            maxHeight: '2.4em',
            textAlign: 'left',
            wordBreak: 'break-word',
            width: '100%'
          }}>
            {inactiveParticipant ? (
              (showEmails ?
                p.required === false ?
                  `${p.email}*` : p.email
                : p.required === false ?
                  (p.name ? `${p.name}*` : `${p.email}*`)
                  : (p.name || p.email)
              ) || `Attendee ${idx + 1}`
            ) : (
              !votable ? '' : (
                p.required === false ?
                  `${p.name || p.email}*`
                  : p.name || p.email
              )
            )}
          </Typography>
        </Box>
        {inAdmin && inactiveParticipant && (
          <CabTooltip
            title={!p.first_response_date && p.selectedSlots.length === 0 ? 
              "There are no responses for this attendee" : 
              p.selectedSlots.length === 0 ? 
                "No times work for this attendee" : 
                ""
            }
            placement="right"
            wrapWithSpan
            spanSx={{ height: '100%' }}
          >
            <Box sx={{ height: '100%' }}>
              {p.selectedSlots.length === 0 && (
                <CabIcon
                  Icon={!p.first_response_date && p.selectedSlots.length === 0 ? IoTimeOutline : IoWarningOutline}
                  sx={{ fontSize: 18 }}
                />
              )}
            </Box>
          </CabTooltip>
        )}
      </Box>
      <Swiper
        modules={modules}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        slidesPerView={slidesPerView}
        slidesPerGroup={slidesPerGroup}
        watchSlidesProgress
        allowTouchMove={false}
        resizeObserver
        virtual={shouldUseVirtual ? {
          enabled: true,
          cache: false,
          addSlidesBefore: 5,
          addSlidesAfter: 5,
        } : undefined}
        pagination={showPagination ? {
          clickable: true,
          renderBullet: function (index, className) {
            return '<span class="' + className + '"></span>';
          },
        } : false}
        style={{ width: '100%', margin: 0, ...swiperStyle }}
      >
        {slots.map((slot, slotIdx) => {
          const isSelectedDate = calendarDate ? slot.start.hasSame(calendarDate, 'day') : false;
          
          let chosenSlot;
          if (inactiveParticipant) {
            chosenSlot = Array.from(p.selectedSlots.values())
              .find(s => s && s.start.equals(slot.start) && s.end.equals(slot.end));
          } else {
            chosenSlot = participant.email && slot ? selectedSlots?.[participant.email]?.get(slot.index) : null;
          }

          const noteText = chosenSlot?.note ?? null;
          const hasVote = chosenSlot?.priority != null;
          const hasNote = hasVote && noteText != null && noteText.trim() !== "";
          // In the voting view, we don't want comment UI on "inactive participant" cells
          // (i.e. the "Current Votes" section). In admin view we do.
          const showInactiveNoteUI = !!inAdmin || !inactiveParticipant;
          const hasNoteForUI = hasNote && showInactiveNoteUI;
          const tooltipIsInteractive = !inactiveParticipant && !!votable && hasVote && (
            hasNoteForUI ? !!onOpenSlotNoteModal || !!onRemoveSlotNote : !!onOpenSlotNoteModal
          );
          const tooltipAuthorName = inactiveParticipant
            ? (p.name || `Attendee ${idx + 1}`)
            : (participant.name || `Attendee`);
          const tooltipAuthorInitial = tooltipAuthorName.trim().slice(0, 1).toUpperCase();
          const tooltipKey = `${participant.email || idx}-${slot.index}`;
          
          return (
            <SwiperSlide
              key={`${participant.email || idx}-slot-${slot.converted.start.toISO()}-${slot.converted.end.toISO()}`}
              virtualIndex={shouldUseVirtual ? slotIdx : undefined}
              className={shouldUseVirtual && swiperSlideStyle?.border ? 'swiper-slide-with-border' : undefined}
              data-is-first-row={shouldUseVirtual && swiperSlideStyle?.border ? isFirstRow : undefined}
              data-is-last-row={shouldUseVirtual && swiperSlideStyle?.border ? isLastRow : undefined}
              style={{
                ...swiperSlideStyle,
                width: 'auto', maxWidth: '200px', overflow: 'hidden', zIndex: -slotIdx,
                boxSizing: 'border-box',
              }}
            >
              <Box sx={{ height: 50 }}>
                <Box
                  sx={[{
                    width: '100%',
                    height: '100%',
                    padding: 1,
                    display: inactiveParticipant && !chosenSlot ? 'none' : 'flex',
                    alignItems: "center",
                    justifyContent: "center"
                  }, ...(inactiveParticipant ? [{
                    backgroundColor: isSelectedDate || !calendarOpen
                      ? 'unset' : alpha(colors.black900, 0.05),
                  }] : [])]}>
                  <CabTooltip
                    disableInteractive={!tooltipIsInteractive}
                    open={tooltipIsInteractive ? openInteractiveTooltipKey === tooltipKey : undefined}
                    onOpen={tooltipIsInteractive ? () => openInteractiveTooltip(tooltipKey) : undefined}
                    onClose={tooltipIsInteractive ? scheduleCloseInteractiveTooltip : undefined}
                    leaveDelay={tooltipIsInteractive ? 250 : undefined}
                    componentsProps={
                      {
                        popper: tooltipIsInteractive ? {
                          onMouseEnter: () => openInteractiveTooltip(tooltipKey),
                          onMouseLeave: scheduleCloseInteractiveTooltip,
                        } : undefined,
                        tooltip: {
                          sx: {
                            backgroundColor: colors.white900,
                            color: colors.black900,
                            border: `1px solid ${colors.black200}`,
                            boxShadow: "none",
                            "& .MuiTooltip-arrow": {
                              color: colors.white900,
                            },
                          },
                        },
                      }
                    }
                    title={
                      inactiveParticipant
                        ? (hasNoteForUI
                          ? (
                            <Box
                              sx={{
                                minWidth: 250,
                                maxWidth: 280,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.75,
                                p: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: colors.lavendar100,
                                      color: colors.black900,
                                      fontWeight: 700,
                                      fontSize: 12,
                                    }}
                                  >
                                    {tooltipAuthorInitial}
                                  </Avatar>
                                </Box>

                                <Box sx={{ width: 28 }} />
                              </Box>

                              <Typography
                                sx={{
                                  fontSize: 13,
                                  color: colors.black800,
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {noteText || ""}
                              </Typography>
                            </Box>
                          )
                          : (p.name || `Attendee ${idx + 1}`)
                        )
                        : (!hasVote
                          ? undefined
                          : hasNoteForUI
                            ? (
                              <Box
                                sx={{
                                  minWidth: 250,
                                  maxWidth: 280,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.75,
                                  p: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        bgcolor: colors.lavendar500,
                                        color: colors.black900,
                                        fontWeight: 700,
                                        fontSize: 12,
                                      }}
                                    >
                                      {tooltipAuthorInitial}
                                    </Avatar>
                                  </Box>

                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {onOpenSlotNoteModal && (
                                      <CabButton
                                        buttonType="text"
                                        icon={<CabIcon size="small" Icon={IoCreateOutline} />}
                                        sx={{ minWidth: 32, height: 32, padding: 0 }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setOpenInteractiveTooltipKey(null);
                                          onOpenSlotNoteModal({
                                            participantEmail: participant.email as string,
                                            slotIndex: slot.index,
                                            slot: chosenSlot as SelectedSlot,
                                            mode: "edit",
                                          });
                                        }}
                                      />
                                    )}
                                    {onRemoveSlotNote && (
                                      <CabButton
                                        buttonType="text"
                                        color="error"
                                        icon={<CabIcon size="small" Icon={IoTrashOutline} />}
                                        sx={{ minWidth: 32, height: 32, padding: 0 }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setOpenInteractiveTooltipKey(null);
                                          onRemoveSlotNote({
                                            participantEmail: participant.email as string,
                                            slotIndex: slot.index,
                                            slot: chosenSlot as SelectedSlot,
                                          });
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>

                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: colors.black800,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {noteText || ""}
                                </Typography>
                              </Box>
                            )
                            : onOpenSlotNoteModal
                              ? (
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  <CabButton
                                    buttonType="text"
                                    icon={<Box sx={{
                                      position: "relative"
                                    }}>
                                      <CabIcon Icon={IoChatbubbleOutline} sx={{ fontSize: 22 }} />
                                      <CabIcon Icon={IoAdd}
                                        sx={{ position: "absolute", bottom: 3, right: 3, fontSize: 16 }}
                                      />
                                    </Box>}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setOpenInteractiveTooltipKey(null);
                                      onOpenSlotNoteModal({
                                        participantEmail: participant.email as string,
                                        slotIndex: slot.index,
                                        slot: chosenSlot as SelectedSlot,
                                        mode: "add",
                                      });
                                    }}
                                  >
                                    Add note
                                  </CabButton>
                                </Box>
                              )
                              : undefined
                        )}
                    placement="bottom"
                    wrapWithSpan
                    spanSx={{ width: '100%', height: '100%' }}
                    key={!inactiveParticipant ? participant.email : undefined}
                  >
                    <Box
                      onClick={!inactiveParticipant && votable && onCheckSlot && participant.email
                        ? () => onCheckSlot(participant.email || '', slot.index, chosenSlot?.priority ?? null)
                        : undefined
                      }
                      sx={{
                        display: 'flex',
                        backgroundColor: inactiveParticipant
                          ? (chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                            ? isSelectedDate || !calendarOpen
                              ? colors.forest400 : lighten(colors.forest400, .3)
                            : isSelectedDate || !calendarOpen ? colors.chamomile300 : 'transparent')
                          : (chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                            ? colors.forest400
                            : chosenSlot?.priority === MeetingPollPriority.POTENTIAL
                              ? colors.chamomile300
                              : 'transparent'),
                        border: inactiveParticipant
                          ? (isSelectedDate || !calendarOpen
                            ? `2px solid ${chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                              ? colors.forest700 : colors.chamomile700}`
                            : `2px solid ${lighten(chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                              ? colors.forest700 : colors.chamomile700, .3)}`)
                          : (chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                            ? `2px solid ${colors.forest700}`
                            : chosenSlot?.priority === MeetingPollPriority.POTENTIAL
                              ? `2px solid ${colors.chamomile700}`
                              : `2px solid ${colors.black200}`),
                        height: '100%',
                        width: '100%',
                        borderRadius: 4,
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: !inactiveParticipant && votable ? 'pointer' : undefined
                      }}
                    >
                      <CabIcon
                        Icon={hasNoteForUI
                          ? IoChatbubble
                          : chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                            ? IoCheckmarkCircleOutline
                            : chosenSlot?.priority === MeetingPollPriority.POTENTIAL
                              ? IoHelpCircleOutline
                              : IoEllipseOutline
                        }
                        alt={inactiveParticipant ? p.name : undefined}
                        sx={{
                          fontSize: 24,
                          color: hasNoteForUI
                            ? (chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                              ? colors.forest800
                              : chosenSlot?.priority === MeetingPollPriority.POTENTIAL
                                ? colors.chamomile700
                                : colors.black400)
                            : chosenSlot?.priority === MeetingPollPriority.AVAILABLE
                              ? colors.forest800
                              : chosenSlot?.priority === MeetingPollPriority.POTENTIAL
                                ? colors.chamomile700
                                : colors.black400,
                          pointerEvents: inactiveParticipant ? 'none' : undefined,
                        }}
                      />
                    </Box>
                  </CabTooltip>
                </Box>
              </Box>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <Box
        sx={{
          width: rightGutterWidth,
          minWidth: rightGutterWidth,
          display: "flex",
          alignItems: "center",
          justifyContent: "left"
        }} />
    </Box>
  );
};


interface PaginationSwiperProps {
  slots: (Slot & { index: number; converted: Slot })[];
  slidesPerView: number;
  slidesPerGroup: number;
  onRegisterSwiper?: (swiper: SwiperType) => void;
  inAdmin?: boolean;
  shouldUseVirtual?: boolean;
}

const PaginationSwiper = ({
  slots, slidesPerView, slidesPerGroup, onRegisterSwiper, inAdmin, shouldUseVirtual = false,
}: PaginationSwiperProps): ReactElement => {
  const { leftGutterWidth, rightGutterWidth } = useResponsiveGutterWidths(inAdmin);
  const modules = useMemo(() => {
    const mods = [Controller, Pagination];
    if (shouldUseVirtual) {
      mods.push(Virtual);
    }
    return mods;
  }, [shouldUseVirtual]);
  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    if (swiperRef.current && onRegisterSwiper) {
      onRegisterSwiper(swiperRef.current);
    }
  }, [onRegisterSwiper]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 1,
        width: "100%",
        overflow: 'hidden'
      }}>
      <Box
        sx={{
          width: leftGutterWidth,
          minWidth: leftGutterWidth
        }} />
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <Swiper
          modules={modules}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          slidesPerView={slidesPerView}
          slidesPerGroup={slidesPerGroup}
          watchSlidesProgress
          allowTouchMove={false}
          style={{ width: '100%' }}
          resizeObserver
          virtual={shouldUseVirtual ? {
            enabled: true,
            cache: false,
            addSlidesBefore: 5,
            addSlidesAfter: 5,
          } : undefined}
          pagination={{
            clickable: true,
            renderBullet: function (index, className) {
              return '<span class="' + className + '"></span>';
            },
          }}
        >
          {slots.map((slot, slotIdx) => (
            <SwiperSlide 
              key={`pagination-${slot.converted.start.toISO()}`}
              virtualIndex={shouldUseVirtual ? slotIdx : undefined}
            >
              <Box sx={{
                height: 30
              }} />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
      <Box
        sx={{
          width: rightGutterWidth,
          minWidth: rightGutterWidth
        }} />
    </Box>
  );
};

interface TimeSlotOptionProps {
  slot: (Slot & { index: number; converted: Slot });
  onConvertToMeeting?: (start: DateTime, end: DateTime) => void;
  onRemoveSlot?: (index: number) => void;
  startTime?: string|null;
  isCheckedAvailable: boolean;
  isCheckedPotential: boolean;
  isPrimaryHighlighted: boolean;
  onClick?: () => void;
  isDateSelected?: boolean;
  isMarkedForRemoval?: boolean;
  totalAvailableVotes?: number;
  totalPotentialVotes?: number;
  inProgress?: boolean;
  date?: string|null;
  showVoteCounts?: boolean;
  participantsCount?: number;
  allDaySlots?: boolean;
}

const TimeSlotOption = ({
  slot, onConvertToMeeting, startTime, isCheckedAvailable, isCheckedPotential, isPrimaryHighlighted, onClick,
  isDateSelected, onRemoveSlot, isMarkedForRemoval, totalAvailableVotes, totalPotentialVotes, inProgress, date,
  showVoteCounts = false, participantsCount = 0, allDaySlots = false,
}: TimeSlotOptionProps): ReactElement => {
  const [converting, setConverting] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleConvertToMeeting = onConvertToMeeting ? (async() => {
    setConverting(true);
    await onConvertToMeeting(slot.start, slot.end);
    setConverting(false);
  }) : undefined;

  /* const showVotes = totalAvailableVotes != null && totalPotentialVotes != null
    && (totalAvailableVotes + totalPotentialVotes > 0); */
   
  const isBookedTime = startTime && !inProgress && DateTime.fromISO(startTime).equals(slot.start);

  const backgroundColor = useMemo(() => {
    if (isMarkedForRemoval) {
      return alpha(colors.redError || '#D32F2F', 0.3);
    }
    if (isBookedTime) {
      return colors.lavendar700;
    }
    if (isPrimaryHighlighted) {
      return colors.lavendar100;
    }
    return colors.calmGrey50;
  }, [isMarkedForRemoval, isBookedTime, isPrimaryHighlighted]);

  const color = useMemo(() => {
    if (isBookedTime) {
      return colors.white900;
    }
    return isDateSelected ? colors.black700 : colors.black400;
  }, [isBookedTime, isDateSelected]);

  const formattedDate = isSmallScreen 
    ? slot.converted.start.toLocaleString({ month: 'short', day: 'numeric' })
    : slot.converted.start.toLocaleString({ weekday: 'short', month: 'short', day: 'numeric' });
  // eslint-disable-next-line max-len
  const formattedTime = `${slot.converted.start.toFormat('h:mma').toLowerCase()} - ${slot.converted.end.toFormat('h:mma').toLowerCase()}`;

  return (
    <Box
      onClick={onClick}
      sx={{ cursor: onClick ? "pointer" : undefined, flexGrow: 1, display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: backgroundColor,
        borderColor: isBookedTime ? colors.lavendar700
          : isPrimaryHighlighted ? colors.lavendar300 : (isDateSelected ? colors.black200 : undefined),
        borderWidth: isPrimaryHighlighted || isBookedTime ? 2 : 0,
        borderStyle: 'solid',
        borderRadius: 1,
        paddingLeft: .25,
        paddingRight: .25,
        marginLeft: 0.5,
        marginRight: 0.5,
        textAlign: "center",
        color: color,
        height: '100%',
      }} >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}>
          <Box
            sx={{
              fontWeight: 500,
              fontSize: 12,
              whiteSpace: "nowrap",
              paddingBottom: 1,
              userSelect: 'none'
            }}>
            {formattedDate}
          </Box>
          <Box
            sx={{
              fontWeight: 500,
              fontSize: 12,
              userSelect: 'none',
              color: isBookedTime ? colors.white900 : colors.black900,
              whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
              textAlign: 'center',
              padding: isSmallScreen ? '4px 2px' : '0 0 0.5px 0'
            }}>
            {!allDaySlots ? formattedTime : null}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 0.5,
              justifyContent: "center",
              alignItems: "center"
            }}>
            {handleConvertToMeeting && inProgress && !onRemoveSlot && (
              <CabTooltip title="Schedule meeting for this time" wrapWithSpan>
                <CabButton
                  buttonType="text"
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConvertToMeeting();
                  }}
                  disabled={converting}
                  icon={converting
                    ? <CabSpinner scale={0.7} />
                    : <CabIcon size="small" Icon={IoCalendarClearOutline} color={colors.lavendar700} />
                  }
                  sx={{
                    '& .MuiButton-startIcon': { 
                      margin: 0,
                      marginRight: 0,
                      marginLeft: 0
                    },
                    '& .button-text': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%'
                    },
                    padding: '8px 8px 8px 9px',
                    marginBottom: 1,
                    minWidth: '38px',
                    width: '38px',
                    height: '36px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    transition: 'all 0.2s ease-in-out',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: colors.lavendar700,
                    borderRadius: '18px',
                    '&:hover': {
                      width: '90px',
                      borderColor: colors.lavendar800,
                      '& .book-text': {
                        opacity: 1,
                        marginLeft: '4px',
                        width: 'auto',
                        minWidth: 'auto'
                      }
                    },
                    '& .book-text': {
                      opacity: 0,
                      marginLeft: 0,
                      transition: 'all 0.2s ease-in-out',
                      whiteSpace: 'nowrap',
                      width: 0,
                      minWidth: 0,
                      overflow: 'hidden'
                    }
                  }}
                >
                  <span className="book-text">Book</span>
                </CabButton>
              </CabTooltip>
            )}
            {onRemoveSlot && (
              <CabTooltip title="Remove this time slot">
                <CabButton
                  buttonType="text"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveSlot(slot.index);
                  }}
                  icon={<CabIcon size="small" Icon={IoTrashOutline} />}
                  sx={{ '& .MuiButton-startIcon': { margin: 0 }, padding: 0.5, minWidth: 'auto' }}
                />
              </CabTooltip>
            )}
            {/* {showVotes && !onRemoveSlot && (
              <CabTooltip
                title={[
                  `${totalAvailableVotes ? `${totalAvailableVotes} available` : ''}`,
                  `${totalPotentialVotes ? `${totalPotentialVotes} potentially available` : ''}`
                ].filter(t => !!t).join(', ')}
              >
                <Typography
                  variant="caption" display="flex" alignItems="center" gap={0.5}
                  sx={{ color: colors.black600, fontWeight: 500 }}
                >
                  {totalAvailableVotes! + totalPotentialVotes!}
                </Typography>
              </CabTooltip>
            )} */}
          </Box>
          {showVoteCounts && (() => {
            const votes = (totalAvailableVotes ?? 0) + (totalPotentialVotes ?? 0);
            return (
              <Typography
                variant="caption"
                sx={{
                  color: isBookedTime ? colors.white900 : colors.black600,
                  fontWeight: 500,
                  fontSize: 12,
                  marginTop: 0.5,
                  marginBottom: 1,
                }}
              >
                {votes === 1 ? '1 vote' : `${votes} votes`}
              </Typography>
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
};
